import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../contexts/auth-context";
import { useHistory } from "react-router-dom";
import PieChartCard from "./Helpers/Charts/PieChartCard";
import LineChartCard from "./Helpers/Charts/LineChartCard";
import FederationSummaryCard from "./Helpers/Charts/FederationSummaryCard";
import DataTable, { DataTableColumn, DataTableRow } from "./Helpers/DataTable";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import { useSidebar } from "../NewSideBar/SidebarContext";
import DatePickerModal from "../DatePicker";
import DrillDownDrawer, {
  DrillDownTarget,
  DrillEndpoint,
  MonthOption,
} from "./Helpers/DrillDownDrawer";
import LayoutContainer from "../LayoutContainer";
import styles from "./organizationDashboard.module.css";
import KPICardContainer from "./Helpers/KPICard/KPICardContainer";
import { KPICardData } from "./constant";
import { useOrgDashboardContext } from "../../contexts/dashboard-context/OrgDashboardContext";
import {
  useOrgScope,
  serializeFromOrganization,
} from "../../contexts/dashboard-context/OrgScopeContext";
import SearchableDropdown from "../SearchableDropdown";
import config from "../../config";
import SimpleBackIcon from "../../assets/simpleBackIcon.svg";
import PageNotFound from "../PageNotFound";
import InforIcon from "../../assets/InfoCircleIcon";
import InfoPopup from "../InfoPopup";
import { formatAdserverType, formatDateTime } from "../../utils";
import { OI } from "../../onam/mark";
import { FEATURES } from "../../onam/features";

export const formatCount = (n: number): string => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return String(n);
};

const toRelativeTime = (date: Date): string => {
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days !== 1 ? 's' : ''} ago`;
};

const fmtLocalDate = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const defaultDateRange = () => {
  const now = new Date();
  return {
    start: fmtLocalDate(
      new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    ),
    end: fmtLocalDate(now),
  };
};

const OrganizationDashboard: React.FC = () => {
  const storedPermissions = JSON.parse(localStorage.getItem("userPermissions") || "{}");
  const hasOrganizationDashboardAccess = ["read", "full"].includes(storedPermissions?.organization_dashboard);

  const history = useHistory();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [showTabInfoPopup, setShowTabInfoPopup] = useState(false);
  const [dateRange, setDateRange] = useState(defaultDateRange);

  const {
    dashboardData,
    isLoading,
    isFulfilled,
    lastRefreshedAt,
    refetch,
    dataErrors,
  } = useOrgDashboardContext();
  const { orgId, setOrgId, fromOrganization, setFromOrganization } =
    useOrgScope();
  const { setBreadcrumbItems } = useBreadcrumb();
  const { mainMenuItems, setActiveSubItem } = useSidebar();

  const {
    orgTableGetOrganizationActivationEnrichmentMonthLevelCount,
    orgTableGetOrganizationActivationEnrichmentDayLevelCount,
    appMetadata,
    getOrganizations,
  } = useContext(AuthContext);

  // Admin detection — same pattern as DatasetManager
  const authRole =
    (appMetadata && (Object.values(appMetadata) as any[])[0]?.roles?.[0]) || "";
  const isAdmin =
    authRole === config.superUserRole || authRole === config.applicationAdminRole;
  const isOrgAdmin = authRole === "organization_admin";

  const [selectedOrgName, setSelectedOrgName] = useState<string>("");
  const [adserverEventType, setAdserverEventType] = useState<string | null>(
    null,
  );

  // Drill-down drawer
  const [drillTarget, setDrillTarget] = useState<DrillDownTarget | null>(null);

  // Activation enrichment table month filter + day-level data
  const [selectedTableMonth, setSelectedTableMonth] = useState<string | null>(
    null,
  );
  const [tableDayRows, setTableDayRows] = useState<DataTableRow[]>([]);
  const [tableDayLoading, setTableDayLoading] = useState(false);

  // Dedicated monthly table data (from table-specific API)
  const [tableMonthData, setTableMonthData] = useState<any[]>([]);
  const [tableMonthLoading, setTableMonthLoading] = useState(false);
  const [tableMonthError, setTableMonthError] = useState(false);

  const handleOrgSelect = (org: { id: string; name: string }) => {
    setOrgId(org.id);
    setSelectedOrgName(org.name);
    setSelectedTableMonth(null);
    setTableMonthData([]);
  };
  const handleOrgClear = () => {
    const fallback = localStorage.getItem("organizationkey") ?? "";
    setOrgId(fallback);
    setSelectedOrgName("");
    setSelectedTableMonth(null);
    setTableMonthData([]);
  };

  const tabLabel = (value: boolean | undefined): string =>
    value === undefined ? "All" : value ? "Initiated" : "Target";

  const activeTabLabel = tabLabel(fromOrganization);

  const handleMainTabClick = (target: boolean | undefined) => {
    if (fromOrganization === target) return;
    setAdserverEventType(null);
    setSelectedTableMonth(null);
    setChartResetKey((k) => k + 1);
    setFromOrganization(target);
  };

  const [chartResetKey, setChartResetKey] = useState(0);
  const [tableTab, setTableTab] = useState<"activation" | "enrichment">(
    "activation",
  );

  const clearAllFilters = () => {
    setAdserverEventType(null);
    setSelectedTableMonth(null);
    setChartResetKey((k) => k + 1);
    const defaultRange = defaultDateRange();
    setDateRange(defaultRange);
    refetch(defaultRange.start, defaultRange.end);
  };

  // Build start/end ISO dates for a given month index + year.
  // Uses local date parts (not toISOString) to avoid UTC timezone shift.
  const buildMonthRange = (
    year: number,
    monthIdx: number,
  ): { startDate: string; endDate: string } => {
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    return {
      startDate: fmt(new Date(year, monthIdx, 1)),
      endDate: fmt(new Date(year, monthIdx + 1, 0)),
    };
  };

  // Derive the list of months covered by the current date range
  const availableMonths = (): MonthOption[] => {
    if (!dateRange.start || !dateRange.end) return [];
    const months: MonthOption[] = [];
    // Parse as local time (append T00:00:00) to avoid UTC date shift
    const start = new Date(dateRange.start + "T00:00:00");
    const end = new Date(dateRange.end + "T00:00:00");
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur <= end) {
      const { startDate, endDate } = buildMonthRange(
        cur.getFullYear(),
        cur.getMonth(),
      );
      months.push({
        label: cur.toLocaleDateString("en-US", {
          month: "short",
          year: "numeric",
        }),
        startDate,
        endDate,
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return months;
  };

  useEffect(() => {
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "Organization Dashboard", path: "/organization-dashboard" },
    ]);
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/organization-dashboard")),
      )?.label;
    setActiveSubItem(sideBarMenuToHighlight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch monthly table data from dedicated table API whenever date range changes
  useEffect(() => {
    if (!dateRange.start || !dateRange.end) return;
    const controller = new AbortController();
    setTableMonthLoading(true);
    setTableMonthError(false);
    setTableMonthData([]);
    orgTableGetOrganizationActivationEnrichmentMonthLevelCount({
      args: {
        org_id: orgId,
        start_date: dateRange.start,
        end_date: dateRange.end,
        from_organization: serializeFromOrganization(fromOrganization),
      },
      signal: controller.signal,
    })
      .then((res: any) => {
        if (!controller.signal.aborted) {
          const data: any[] = res?.data ?? [];
          setTableMonthData(data);
          setTableMonthLoading(false);
          // Auto-select the tab that has data if the current tab would be empty
          setTableTab((prev) => {
            const hasActivation = data.some(
              (r) =>
                String(r.activation_enrichment).toLowerCase() === "activation",
            );
            const hasEnrichment = data.some(
              (r) =>
                String(r.activation_enrichment).toLowerCase() === "enrichment",
            );
            if (prev === "activation" && !hasActivation && hasEnrichment)
              return "enrichment";
            if (prev === "enrichment" && !hasEnrichment && hasActivation)
              return "activation";
            return prev;
          });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setTableMonthError(true);
          setTableMonthLoading(false);
        }
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end, orgId, fromOrganization]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch day-level org data when a specific month is selected in the table
  useEffect(() => {
    if (!selectedTableMonth) return;
    const parsed = new Date(`${selectedTableMonth} 1 00:00:00`);
    if (isNaN(parsed.getTime())) return;
    const { startDate, endDate } = buildMonthRange(
      parsed.getFullYear(),
      parsed.getMonth(),
    );
    const controller = new AbortController();
    setTableDayLoading(true);
    setTableDayRows([]);
    orgTableGetOrganizationActivationEnrichmentDayLevelCount({
      args: {
        org_id: orgId,
        start_date: startDate,
        end_date: endDate,
        from_organization: serializeFromOrganization(fromOrganization),
      },
      signal: controller.signal,
    })
      .then((res: any) => {
        if (!controller.signal.aborted) {
          const data: any[] = res?.data ?? [];
          const grouped: Record<string, any> = {};
          for (const r of data) {
            const key = `${r.event_type}::${r.activation_enrichment}`;
            if (!grouped[key]) {
              grouped[key] = {
                event_type: r.event_type ?? "",
                activation_enrichment: r.activation_enrichment ?? "",
                total_count: 0,
                query_count: 0,
              };
            }
            grouped[key].total_count += r.total_count ?? 0;
            grouped[key].query_count += r.query_count ?? 0;
          }
          setTableDayRows(
            Object.values(grouped).map((r, i) => ({
              id: String(i),
              event_type: r.event_type,
              activation_enrichment: r.activation_enrichment,
              total_count: r.total_count,
              query_count: r.query_count,
            })),
          );
          setTableDayLoading(false);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setTableDayLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableMonth, fromOrganization]);

  const openDrill = (
    cardTitle: string,
    eventType?: string | null,
    monthLabelStr?: string,
    endpoint?: DrillEndpoint,
    entityParams?: Record<string, string>,
    chartOptions?: {
      dayLevelChartMetric?: "total_count" | "query_count";
      allowedDimensionKeys?: string[];
    },
  ) => {
    const months = availableMonths();
    if (monthLabelStr) {
      const [mon, yr] = monthLabelStr.split(" ");
      const monthIdx = new Date(`${mon} 1 ${yr} 00:00:00`).getMonth();
      const { startDate } = buildMonthRange(parseInt(yr, 10), monthIdx);
      setDrillTarget({
        cardTitle,
        availableMonths: months,
        defaultMonthStartDate: startDate,
        eventType: eventType ?? undefined,
        endpoint,
        entityParams,
        ...chartOptions,
      });
    } else {
      setDrillTarget({
        cardTitle,
        availableMonths: months,
        eventType: eventType ?? undefined,
        endpoint,
        entityParams,
        ...chartOptions,
      });
    }
  };

  const handleDateApply = (start: string, end: string) => {
    const s = start.split("T")[0];
    const e = end.split("T")[0];
    setDateRange({ start: s, end: e });
    refetch(s, e);
  };

  const formatDateLabel = () => {
    if (!dateRange.start || !dateRange.end) return "Select date range";
    const fmt = (d: string) => {
      const [y, m, day] = d.split("-");
      return `${day}/${m}/${y}`;
    };
    return `${fmt(dateRange.start)} - ${fmt(dateRange.end)}`;
  };

  const activeFedCount = (() => {
    if (!dashboardData) return 0;
    if (dashboardData.uniqueActiveFederationCount !== null)
      return dashboardData.uniqueActiveFederationCount;
    return new Set(
      dashboardData.federationMonth
        .filter((r) => r.total_count > 0 || r.query_count > 0)
        .map((r) => r.federation_id),
    ).size;
  })();

  const kpiCards = dashboardData
    ? [
        {
          ...KPICardData[0],
          value: activeFedCount,
        },
        {
          ...KPICardData[1],
          value: formatCount(
            dashboardData.eventType.find((e) => e.event_type === "query")
              ?.query_count ?? 0,
          ),
        },
        {
          ...KPICardData[2],
          value: formatCount(
            dashboardData.eventType.find(
              (e) => e.event_type === "marked_engaged",
            )?.query_count ?? 0,
          ),
        },
        {
          ...KPICardData[3],
          value: formatCount(
            dashboardData.eventType.find(
              (e) => e.event_type === "audience_transferred",
            )?.total_count ?? 0,
          ),
          rawValue:
            dashboardData.eventType.find(
              (e) => e.event_type === "audience_transferred",
            )?.total_count ?? 0,
        },
      ]
    : KPICardData;

  const pieItems = dashboardData
    ? [
        {
          name: "Query",
          total_count:
            dashboardData.eventType.find((e) => e.event_type === "query")
              ?.total_count ?? 0,
          query_count:
            dashboardData.eventType.find((e) => e.event_type === "query")
              ?.query_count ?? 0,
          color: "#0D6AB3",
        },
        {
          name: "Marked/Engaged",
          total_count:
            dashboardData.eventType.find(
              (e) => e.event_type === "marked_engaged",
            )?.total_count ?? 0,
          query_count:
            dashboardData.eventType.find(
              (e) => e.event_type === "marked_engaged",
            )?.query_count ?? 0,
          color: "#2ACDFF",
        },
        {
          name: "Audience Transferred",
          total_count:
            dashboardData.eventType.find(
              (e) => e.event_type === "audience_transferred",
            )?.total_count ?? 0,
          query_count:
            dashboardData.eventType.find(
              (e) => e.event_type === "audience_transferred",
            )?.query_count ?? 0,
          color: "#F59E0B",
        },
      ]
    : [];

  // Colour palette shared across the three summary cards
  const ENTITY_COLORS = [
    "#2563EB",
    "#F59E0B",
    "#10B981",
    "#EF4444",
    "#8B5CF6",
    "#06B6D4",
    "#F97316",
    "#84CC16",
    "#EC4899",
    "#14B8A6",
  ];

  // Helper: pivot entity-level month data into per-entity lines.
  // Each data point: { month_label, {entityKey}_total, {entityKey}_query }
  // Lines: one per active entity (non-zero data).
  function pivotByEntity<
    T extends {
      total_count: number;
      query_count: number;
      month: number;
      month_name: string;
      year: number;
      [key: string]: any;
    },
  >(
    rows: T[],
    getKey: (r: T) => string,
    getLabel: (key: string) => string,
    getMonthLabel: (r: T) => string,
  ) {
    const activeKeys = Array.from(
      new Set(
        rows.filter((r) => r.total_count > 0 || r.query_count > 0).map(getKey),
      ),
    );
    const monthMap: Record<string, Record<string, any>> = {};
    const monthSortKeys: Record<string, number> = {};
    for (const r of rows) {
      const month = getMonthLabel(r);
      if (!monthMap[month]) monthMap[month] = { month_label: month };
      monthSortKeys[month] = r.year * 12 + r.month;
      const k = getKey(r);
      monthMap[month][`${k}_total`] =
        (monthMap[month][`${k}_total`] ?? 0) + r.total_count;
      monthMap[month][`${k}_query`] =
        (monthMap[month][`${k}_query`] ?? 0) + r.query_count;
    }
    // Fill every active key to 0 for months where it has no data,
    // so Recharts shows all entries in the tooltip (not just defined ones).
    for (const monthData of Object.values(monthMap)) {
      for (const k of activeKeys) {
        if (monthData[`${k}_total`] === undefined) monthData[`${k}_total`] = 0;
        if (monthData[`${k}_query`] === undefined) monthData[`${k}_query`] = 0;
      }
    }

    // Detect duplicate display names — disambiguate by appending short id suffix
    const rawLabels = activeKeys.map((k) => getLabel(k));
    const labelCount: Record<string, number> = {};
    for (const l of rawLabels) labelCount[l] = (labelCount[l] ?? 0) + 1;
    const labelSeen: Record<string, number> = {};
    const disambiguated = activeKeys.map((k) => {
      const label = getLabel(k);
      if (labelCount[label] <= 1) return label;
      labelSeen[label] = (labelSeen[label] ?? 0) + 1;
      // append short suffix from key to disambiguate (e.g. "Group M ·2")
      const suffix = k.split("::")[0]?.slice(0, 4) ?? String(labelSeen[label]);
      return `${label} ·${suffix}`;
    });

    return {
      data: Object.values(monthMap).sort(
        (a, b) =>
          (monthSortKeys[a.month_label] ?? 0) -
          (monthSortKeys[b.month_label] ?? 0),
      ),
      lines: activeKeys.map((k, i) => ({
        key: k,
        name: disambiguated[i],
        color: ENTITY_COLORS[i % ENTITY_COLORS.length],
      })),
    };
  }

  const monthLabel = (r: { month_name: string; year: number }) =>
    `${r.month_name} ${r.year}`;

  // Build label lookup: composite key → display name.
  // Key = federation_id::federation_name so that:
  //   - same id / different names → separate lines (distinct distributor relationships)
  //   - same name / different ids → separate lines (different feds that share a name)
  const fedLabelMap: Record<string, string> = {};
  if (dashboardData) {
    for (const r of dashboardData.federationMonth) {
      const k = `${r.federation_id}::${r.federation_name}`;
      fedLabelMap[k] = r.federation_name || r.federation_id.slice(0, 8);
    }
  }

  // Unique event types per dataset (for sidebar dropdowns)
  const adserverEventTypes = dashboardData
    ? Array.from(
        new Set(
          dashboardData.adserverMonth.map((r) => r.event_type).filter(Boolean),
        ),
      )
    : [];

  // Federation wise: composite key, display name as label
  const { data: federationChartData, lines: federationLines } = dashboardData
    ? pivotByEntity(
        dashboardData.federationMonth,
        (r) => `${r.federation_id}::${r.federation_name}`,
        (k) => fedLabelMap[k] ?? k.split("::")[1] ?? k.slice(0, 8),
        monthLabel,
      )
    : { data: [], lines: [] };

  // Adserver wise
  const { data: adserverChartData, lines: adserverLines } = dashboardData
    ? pivotByEntity(
        adserverEventType
          ? dashboardData.adserverMonth.filter(
              (r) => r.event_type === adserverEventType,
            )
          : dashboardData.adserverMonth,
        (r) => r.destination_adserver_type,
        (type) => formatAdserverType(type),
        monthLabel,
      )
    : { data: [], lines: [] };

  // Pivot eventMonth rows → one object per month with both total_count and query_count per event type
  // Keys: {event_type}_total and {event_type}_count — LineChartCard toggles between them
  const lineChartData = dashboardData
    ? (() => {
        const map: Record<string, Record<string, any>> = {};
        const sortKeys: Record<string, number> = {};
        for (const r of dashboardData.eventMonth) {
          const label = `${r.month_name} ${r.year}`;
          if (!map[label]) map[label] = { month_label: label };
          sortKeys[label] = r.year * 12 + r.month;
          map[label][`${r.event_type}_total`] = r.total_count;
          map[label][`${r.event_type}_count`] = r.query_count;
        }
        return Object.values(map).sort(
          (a, b) =>
            (sortKeys[a.month_label] ?? 0) - (sortKeys[b.month_label] ?? 0),
        );
      })()
    : [];

  // Unique months in order of appearance for the month pill filter
  const tableMonthOptions = Array.from(
    new Set(tableMonthData.map((r) => `${r.month_name} ${r.year}`)),
  );

  // Group by event_type + activation_enrichment (org-scoped API, no per-org breakdown)
  const allTableRows: DataTableRow[] = (() => {
    const grouped: Record<
      string,
      {
        event_type: string;
        activation_enrichment: string;
        total_count: number;
        query_count: number;
      }
    > = {};
    for (const r of tableMonthData) {
      const key = `${r.event_type}::${r.activation_enrichment}`;
      if (!grouped[key]) {
        grouped[key] = {
          event_type: r.event_type,
          activation_enrichment: r.activation_enrichment,
          total_count: 0,
          query_count: 0,
        };
      }
      grouped[key].total_count += r.total_count;
      grouped[key].query_count += r.query_count;
    }
    return Object.values(grouped).map((r, i) => ({
      id: String(i),
      event_type: r.event_type,
      activation_enrichment: r.activation_enrichment,
      total_count: r.total_count,
      query_count: r.query_count,
    }));
  })();

  // Filter monthly rows by active tab
  const filteredTableRows = allTableRows.filter(
    (r) => String(r.activation_enrichment).toLowerCase() === tableTab,
  );

  // Filter day-level rows by active tab (API returns both workflow types)
  const filteredDayRows = tableDayRows.filter(
    (r) => String(r.activation_enrichment).toLowerCase() === tableTab,
  );

  // Switch between monthly and day-level view
  const tableRows = selectedTableMonth ? filteredDayRows : filteredTableRows;
  const tableLoading = selectedTableMonth ? tableDayLoading : tableMonthLoading;

  const formatEventType = (val: string | number) =>
    String(val)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const formatCountVolume = (val: string | number) => {
    return Number(val).toLocaleString("en-US");
  };

  const tableColumns: DataTableColumn[] = selectedTableMonth
    ? [
        {
          label: "Workflow Type",
          key: "activation_enrichment",
          formatter: formatEventType,
        },
        {
          label: "Audience Volume",
          key: "total_count",
          formatter: formatCountVolume,
        },
        { label: "Transfers Completed", key: "query_count" },
      ]
    : [
        { label: "Event Type", key: "event_type", formatter: formatEventType },
        {
          label: "Workflow Type",
          key: "activation_enrichment",
          formatter: formatEventType,
        },
        {
          label: "Audience Volume",
          key: "total_count",
          formatter: formatCountVolume,
        },
        { label: "Transfers Completed", key: "query_count" },
      ];

  if (!hasOrganizationDashboardAccess && !isOrgAdmin) return <PageNotFound />;

  return (
    <LayoutContainer>
      <div className={styles.actionsBar}>
        <div className={styles.actionsBarLeft}>
          <button className={styles.backBtn} onClick={() => history.goBack()}>
            <img src={SimpleBackIcon} alt="back" />
            <span>Back</span>
          </button>
        </div>

        <div className={styles.actionsBarRight} />
      </div>
      {/* White scrollable container */}
      <div className={styles.scrollWrapper}>
        {/* Admin org selector */}
        {isAdmin && (
          <div className={styles.orgSelectorRow}>
            <span className={styles.orgSelectorLabel}>Organization</span>
            <SearchableDropdown<{ id: string; name: string }>
              fetchOptions={(name) =>
                getOrganizations({ args: { name, page_size: 8 } })
              }
              onSelect={handleOrgSelect}
              onClear={handleOrgClear}
              getLabel={(org) => org.name}
              getValue={(org) => org.id}
              selectedValue={orgId}
              selectedLabel={selectedOrgName}
              placeholder="Search organization…"
              className={styles.orgSelectorDropdown}
            />
          </div>
        )}
        <div className={styles.topBar}>
          <span className={styles.lastRefreshed}>
            Last refreshed:{" "}
            {lastRefreshedAt ? (
              <strong
                title={formatDateTime(lastRefreshedAt)}
                style={{ cursor: "default" }}
              >
                {toRelativeTime(lastRefreshedAt)}
              </strong>
            ) : (
              <strong>{isLoading ? "Loading…" : "—"}</strong>
            )}
          </span>
          <div className={styles.tabInfoRow}>
            <div className={styles.mainTabsRow}>
              <button
                className={`${styles.mainTab} ${fromOrganization === undefined ? styles.mainTabActive : ""}`}
                onClick={() => handleMainTabClick(undefined)}
              >
                All
              </button>
              <button
                className={`${styles.mainTab} ${fromOrganization === true ? styles.mainTabActive : ""}`}
                onClick={() => handleMainTabClick(true)}
              >
                Initiated
              </button>
              <button
                className={`${styles.mainTab} ${fromOrganization === false ? styles.mainTabActive : ""}`}
                onClick={() => handleMainTabClick(false)}
              >
                Target
              </button>
            </div>
            <InforIcon
              className={styles.tabInfoIcon}
              onClick={() => setShowTabInfoPopup(true)}
            />
          </div>
          <div className={styles.topBarRight}>
            <button
              className={styles.clearFiltersLink}
              onClick={clearAllFilters}
            >
              Clear filters
            </button>
            <button
              className={`${styles.dateRangeBtn} ${(() => { const d = defaultDateRange(); return dateRange.start !== d.start || dateRange.end !== d.end; })() ? styles.dateRangeBtnActive : ""}`}
              onClick={() => setDatePickerOpen(true)}
            >
              <span>{formatDateLabel()}</span>
              {FEATURES.curationSkin ? <OI n="calendar" size={16} /> : (
<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 4H5C3.9 4 3 4.9 3 6V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V10H19V20ZM19 8H5V6H19V8ZM9 14H7V12H9V14ZM13 14H11V12H13V14ZM17 14H15V12H17V14ZM9 18H7V16H9V18ZM13 18H11V16H13V18ZM17 18H15V16H17V18Z"
                  fill="var(--Primary-Color-Shade-1)"
                />
              </svg>
)}
            </button>
          </div>
        </div>
        {showTabInfoPopup && (
            <InfoPopup
              isOpen={showTabInfoPopup}
              onClose={() => setShowTabInfoPopup(false)}
              closeButtonLabel="Got it"
              headerText="Information"
            >
              <p className={styles.tabInfoIntro}>
                These tabs determine which organizational activity is
                displayed on this dashboard.
              </p>
              <dl className={styles.tabInfoList}>
                <dt>All</dt>
                <dd>
                  Displays all activity associated with your organization,
                  regardless of whether it was initiated by your organization
                  or by another organization.
                </dd>
                <dt>Initiated</dt>
                <dd>Displays activity initiated by your organization.</dd>
                <dt>Target</dt>
                <dd>
                  Displays activity initiated by another organization in
                  which your organization participated as a contributor —
                  for example, by providing data or audience — without
                  having initiated the activity.
                </dd>
              </dl>
              <p className={styles.tabInfoFootnote}>
                Note: The total displayed under the All tab may be
                marginally lower than the combined totals of the Initiated
                and Target tabs. This occurs when activity is both
                initiated and fulfilled by your own organization, in which
                case it is counted once under All rather than twice.
              </p>
            </InfoPopup>
          )}

        <div className={styles.sectionHeading}>
          <span className={styles.sectionLabel}>Event Overview</span>
          <span className={styles.activeTabBadge}>Showing: {activeTabLabel}</span>
          <div className={styles.sectionRule} />
        </div>
        <div className={styles.cardsContainer}>
          <KPICardContainer cards={kpiCards} isLoading={isLoading} />
        </div>

        <div className={styles.sectionHeading}>
          <span className={styles.sectionLabel}>Time Series</span>
          <div className={styles.sectionRule} />
        </div>
        <div className={styles.container}>
          <PieChartCard
            title="Event Type Distribution"
            subtitle=""
            items={pieItems}
            footerText=""
            isLoading={isLoading}
            hasError={isFulfilled && !!dataErrors.eventType}
            onSegmentClick={(eventType) =>
              openDrill("Event Type Distribution", eventType)
            }
          />

          <LineChartCard
            key={chartResetKey}
            title="Month wise event type distribution of count/sum"
            xAxisKey="month_label"

            data={lineChartData}
            yTickFormatter={formatCount}
            isLoading={isLoading}
            hasError={isFulfilled && !!dataErrors.eventMonth}
            lines={[
              { key: "query", name: "Query", color: "#0D6AB3" },
              {
                key: "marked_engaged",
                name: "Marked/Engaged",
                color: "#2ACDFF",
              },
              {
                key: "audience_transferred",
                name: "Audience Transferred",
                color: "#F59E0B",
              },
            ]}
            onMonthClick={(label, metric, visibleKeys) => {
              // Single event type visible → server-side filter via eventType param
              const singleEventType =
                visibleKeys.length === 1 ? visibleKeys[0] : null;
              openDrill(
                "Month wise Event Type Distribution",
                singleEventType,
                label,
                "event",
                undefined,
                {
                  dayLevelChartMetric:
                    metric === "count" ? "query_count" : "total_count",
                  // Client-side filter only when multiple (but not all) event types are visible
                  allowedDimensionKeys:
                    !singleEventType && visibleKeys.length < 3
                      ? visibleKeys
                      : undefined,
                },
              );
            }}
          />

          <FederationSummaryCard
            key={`adserver-${chartResetKey}`}
            title={"Ad Server\nSummary"}
            secondDropdownLabel="Ad Servers"
            queryMetricLabel="Activation Count"
            xAxisKey="month_label"
            data={adserverChartData}
            yTickFormatter={formatCount}
            isLoading={isLoading}
            hasError={isFulfilled && !!dataErrors.adserverMonth}
            lines={adserverLines}
            dateRange={dateRange}
            onMonthClick={(label, metric, selectedKeys) => {
              const allSelected = selectedKeys.length >= adserverLines.length;
              const singleType =
                !allSelected && selectedKeys.length === 1
                  ? selectedKeys[0]
                  : null;
              openDrill(
                "Ad Server Summary",
                null,
                label,
                "adserver",
                singleType
                  ? { destination_adserver_type: singleType }
                  : undefined,
                {
                  dayLevelChartMetric:
                    metric === "query" ? "query_count" : "total_count",
                  allowedDimensionKeys:
                    !allSelected && !singleType ? selectedKeys : undefined,
                },
              );
            }}
          />
        </div>

        <FederationSummaryCard
          key={`fed-${chartResetKey}`}
          title={"Federation wise\nSummary"}
          secondDropdownLabel="Federations"
          xAxisKey="month_label"
          data={federationChartData}
          yTickFormatter={formatCount}
          isLoading={isLoading}
          hasError={isFulfilled && !!dataErrors.federationMonth}
          lines={federationLines}
          dateRange={dateRange}
          onMonthClick={(label, metric, selectedKeys) => {
            const allSelected = selectedKeys.length >= federationLines.length;
            const singleKey =
              !allSelected && selectedKeys.length === 1
                ? selectedKeys[0]
                : null;
            const entityId = singleKey ? singleKey.split("::")[0] : null;
            openDrill(
              "Federation Summary",
              null,
              label,
              "federation",
              entityId ? { federation_id: entityId } : undefined,
              {
                dayLevelChartMetric:
                  metric === "query" ? "query_count" : "total_count",
                allowedDimensionKeys:
                  !allSelected && !entityId
                    ? selectedKeys.map((k) => k.split("::")[0])
                    : undefined,
              },
            );
          }}
        />

        <div className={styles.sectionHeading}>
          <span className={styles.sectionLabel}>Audience Table</span>
          <div className={styles.sectionRule} />
        </div>
        <div className={styles.tableContainer}>
          <DataTable
            title="Audience Transferred"
            subHeader={
              <div className={styles.tableTabsRow}>
                <button
                  className={`${styles.tableTab} ${tableTab === "activation" ? styles.tableTabActive : ""}`}
                  onClick={() => setTableTab("activation")}
                >
                  Activation
                </button>
                <button
                  className={`${styles.tableTab} ${tableTab === "enrichment" ? styles.tableTabActive : ""}`}
                  onClick={() => setTableTab("enrichment")}
                >
                  Enrichment
                </button>
              </div>
            }
            columns={tableColumns}
            rows={tableRows}
            isLoading={tableLoading}
            hasError={tableMonthError}
            monthFilter={{
              options: tableMonthOptions,
              selected: selectedTableMonth,
              onChange: setSelectedTableMonth,
            }}
          />
        </div>
      </div>

      <DatePickerModal
        datePickerOpen={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        onApply={handleDateApply}
        initialStart={dateRange.start ? `${dateRange.start}T00:00` : ""}
        initialEnd={dateRange.end ? `${dateRange.end}T00:00` : ""}
      />

      <DrillDownDrawer
        target={drillTarget}
        onClose={() => setDrillTarget(null)}
      />
    </LayoutContainer>
  );
};

export default OrganizationDashboard;
